import { plans as mockPlans } from '@/mocks/plans';
import type { BillingType, PlanIntervalUnit, PlanStatus, ProductPlan } from '@/domain/types';
import { requireSupabase } from '@/lib/supabase';

// Legacy mock export — used by public product-detail page
export const plans = mockPlans;

// ── Duration helpers ─────────────────────────────────────────────────────────

export function durationToDays(unit: PlanIntervalUnit, count: number): number {
  if (unit === 'year')  return count * 365;
  if (unit === 'month') return count * 30;
  return count;
}

export function formatDuration(unit: PlanIntervalUnit, count: number): string {
  const label = unit.charAt(0).toUpperCase() + unit.slice(1);
  return count === 1 ? `1 ${label}` : `${count} ${label}s`;
}

export function formatPrice(price: number, currency: string, unit: PlanIntervalUnit, billingType: BillingType): string {
  const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
  if (billingType === 'one_time') return `${fmt} one-time`;
  const suffix = unit === 'month' ? '/mo' : unit === 'year' ? '/yr' : '/day';
  return `${fmt}${suffix}`;
}

// ── Row type ─────────────────────────────────────────────────────────────────

type PlanRow = {
  id: string;
  product_id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_type: string;
  interval_unit: string;
  interval_count: number;
  site_limit: number;
  license_duration_days: number | null;
  features: string[];
  is_popular: boolean;
  sort_order: number;
  active: boolean;
  created_at: string;
  products?: { name?: string | null } | null;
};

const PLAN_SELECT = '*, products!product_id(name)';

function mapPlan(row: PlanRow): ProductPlan {
  return {
    id: row.id,
    productId: row.product_id,
    productName: row.products?.name ?? '',
    name: row.name,
    description: row.description ?? '',
    price: Number(row.price),
    currency: row.currency ?? 'USD',
    billingType: (row.billing_type ?? 'subscription') as BillingType,
    intervalUnit: (row.interval_unit ?? 'month') as PlanIntervalUnit,
    intervalCount: row.interval_count ?? 1,
    siteLimit: row.site_limit ?? 1,
    licenseDurationDays: row.license_duration_days,
    features: Array.isArray(row.features) ? row.features : [],
    isPopular: row.is_popular ?? false,
    sortOrder: row.sort_order ?? 0,
    status: (row.active ? 'active' : 'inactive') as PlanStatus,
    createdAt: row.created_at,
  };
}

// ── Read ─────────────────────────────────────────────────────────────────────


export async function getPublicPlansByProduct(productId: string): Promise<ProductPlan[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('plans')
    .select(PLAN_SELECT)
    .eq('product_id', productId)
    .eq('active', true)
    .order('sort_order', { ascending: true })
    .order('price', { ascending: true });
  if (error) throw error;
  return (data as PlanRow[]).map(mapPlan);
}

export async function listPublicPlans(): Promise<ProductPlan[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('plans')
    .select(PLAN_SELECT)
    .eq('active', true)
    .order('sort_order', { ascending: true })
    .order('price', { ascending: true });
  if (error) throw error;
  return (data as PlanRow[]).map(mapPlan);
}

export async function listAdminPlans(): Promise<ProductPlan[]> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('plans')
    .select(PLAN_SELECT)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as PlanRow[]).map(mapPlan);
}


export async function getPlanById(id: string): Promise<ProductPlan | null> {
  const supabase = requireSupabase();
  const { data, error } = await supabase
    .from('plans')
    .select(PLAN_SELECT)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return data ? mapPlan(data as PlanRow) : null;
}

// ── Write ────────────────────────────────────────────────────────────────────

export interface CreatePlanInput {
  name: string;
  description: string;
  price: number;
  currency: string;
  billingType: BillingType;
  intervalUnit: PlanIntervalUnit;
  intervalCount: number;
  siteLimit: number;
  unlimited: boolean;
  features: string[];
  isPopular: boolean;
  sortOrder: number;
  active: boolean;
  productId?: string;
}

export async function createAdminPlan(input: CreatePlanInput): Promise<ProductPlan> {
  const supabase = requireSupabase();
  const licenseDurationDays = input.billingType === 'one_time'
    ? null
    : durationToDays(input.intervalUnit, input.intervalCount);

  if (!input.productId) throw new Error('Product selection is required.');

  const { data, error } = await supabase
    .from('plans')
    .insert({
      product_id:            input.productId,
      name:                  input.name.trim(),
      description:           input.description.trim(),
      price:                 input.price,
      currency:              input.currency,
      billing_type:          input.billingType,
      interval_unit:         input.intervalUnit,
      interval_count:        input.intervalCount,
      site_limit:            input.unlimited ? 999 : input.siteLimit,
      license_duration_days: licenseDurationDays,
      features:              input.features.filter(Boolean),
      is_popular:            input.isPopular,
      sort_order:            input.sortOrder,
      active:                input.active,
    })
    .select(PLAN_SELECT)
    .single();

  if (error) throw error;
  return mapPlan(data as PlanRow);
}

export async function updateAdminPlan(id: string, input: Partial<CreatePlanInput>): Promise<ProductPlan> {
  const supabase = requireSupabase();

  const updates: Record<string, unknown> = {};
  if (input.name        !== undefined) updates.name        = input.name.trim();
  if (input.description !== undefined) updates.description = input.description.trim();
  if (input.price       !== undefined) updates.price       = input.price;
  if (input.currency    !== undefined) updates.currency    = input.currency;
  if (input.billingType !== undefined) updates.billing_type = input.billingType;
  if (input.isPopular   !== undefined) updates.is_popular  = input.isPopular;
  if (input.sortOrder   !== undefined) updates.sort_order  = input.sortOrder;
  if (input.active      !== undefined) updates.active      = input.active;
  if (input.features    !== undefined) updates.features    = input.features.filter(Boolean);

  if (input.unlimited !== undefined || input.siteLimit !== undefined) {
    updates.site_limit = input.unlimited ? 999 : (input.siteLimit ?? 1);
  }

  if (input.intervalUnit !== undefined || input.intervalCount !== undefined) {
    const unit  = input.intervalUnit  ?? 'month';
    const count = input.intervalCount ?? 1;
    updates.interval_unit  = unit;
    updates.interval_count = count;
    const bType = input.billingType ?? 'subscription';
    updates.license_duration_days = bType === 'one_time' ? null : durationToDays(unit, count);
  }

  const { data, error } = await supabase
    .from('plans')
    .update(updates)
    .eq('id', id)
    .select(PLAN_SELECT)
    .single();

  if (error) throw error;
  return mapPlan(data as PlanRow);
}

export async function toggleAdminPlanStatus(id: string, active: boolean): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase.from('plans').update({ active }).eq('id', id);
  if (error) throw error;
}

export async function deleteAdminPlan(id: string): Promise<void> {
  const supabase = requireSupabase();
  const { error } = await supabase.from('plans').delete().eq('id', id);
  if (error) throw error;
}
